/**
 * Component: ChatPage
 * SpecReferenceID: GALEN-CHAT-V1
 * Requirements: GALEN-CHAT-V1-R01, GALEN-CHAT-V1-R02, GALEN-CHAT-V1-R03,
 *               GALEN-CHAT-V1-R04, GALEN-CHAT-V1-R06, GALEN-CHAT-V1-R07,
 *               GALEN-CHAT-V1-R08, GALEN-CHAT-V1-R09, GALEN-CHAT-V1-R13,
 *               GALEN-CHAT-V1-R14, GALEN-CHAT-V1-R15, FRONTEND-WEB-V1-R02,
 *               FRONTEND-WEB-V1-R05, FRONTEND-WEB-V1-R10, FRONTEND-WEB-V1-R13,
 *               FRONTEND-WEB-V1-R14, BROWSE-V1-R20, BROWSE-V1-R21
 *
 * Galen Chat interface for free-text clinical guideline questions.
 * Accepts user questions and displays LLM-generated narrative responses
 * with citations. Supports multi-turn conversations with context.
 * Uses SSE streaming for incremental response rendering.
 * Conversations persist in localStorage via Zustand store.
 *
 * GALEN-CHAT-V1-R01: Accept free-text user questions via chat interface.
 * GALEN-CHAT-V1-R02: Format LLM responses for clinical reading.
 * GALEN-CHAT-V1-R03: Display all citations and referenced sections.
 * GALEN-CHAT-V1-R04: Present citations in expandable format.
 * GALEN-CHAT-V1-R06: Optionally integrate with Semantic Search for grounding.
 * GALEN-CHAT-V1-R07: Display only citations from retrieved documents.
 * GALEN-CHAT-V1-R08: Maintain conversation history until user resets.
 * GALEN-CHAT-V1-R09: Enable follow-up questions with conversation context.
 * GALEN-CHAT-V1-R13: Does NOT display ranked retrieval results (scope boundary).
 * GALEN-CHAT-V1-R14: Provides answer-first interface with LLM narrative.
 * GALEN-CHAT-V1-R15: Does NOT provide browsing or evidence discovery UI -
 *                    that functionality belongs to Galen Search module.
 *                    BROWSE-V1-R20 adds a single cross-module link on
 *                    each chat citation card that opens the dedicated
 *                    BROWSE-V1 viewer in a new tab; the chat surface
 *                    itself remains free of in-chat browsing UI, so
 *                    the R15 scope boundary is preserved.
 * BROWSE-V1-R20: Chat citation card renders a BrowseLink when the
 *                upstream GuidelineId is present and the BrowseLinks
 *                feature flag is on.
 * BROWSE-V1-R21: BrowseLink opens the viewer in a new tab via
 *                target="_blank" and passes guidelineId/sectionId
 *                from the citation payload.
 * FRONTEND-WEB-V1-R02: Collect user input via text or optional audio.
 * FRONTEND-WEB-V1-R05: Render streaming LLM responses incrementally.
 *
 * Accessibility: WCAG 2.1 AA compliant
 */

import React, { useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Send,
  Loader2,
  AlertCircle,
  Bot,
  Copy,
  Check,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Menu,
  User,
  Sparkles,
  Plus,
  Info,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  X,
} from 'lucide-react';
import { format } from 'date-fns';
import { trackUsageAction } from '@/lib/usage-tracking';
import { submitFeedback } from '@/lib/feedback';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useBranding, useIsFeatureEnabled } from '@/contexts';
import { UserInputArea, type RecordingStatusInfo } from '@/components/input/UserInputArea';
import { SafeHtml } from '@/components/SafeHtml';
import { BrowseLink } from '@/features/browse';
import { useStreamingResponse } from '@/hooks/use-streaming-response';
import { ConversationSidebar } from './components/ConversationSidebar';
import { MarkdownRenderer } from './components/MarkdownRenderer';
import {
  useActiveConversation,
  useConversationStore,
} from '@/stores/conversation-store';
import type { ChatMessage, Citation, CitationDisplayMode } from '@/types';

/**
 * Local-only message used during streaming (not persisted until finalized).
 */
interface StreamingMessage {
  id: string;
  role: 'assistant';
  content: string;
  isStreaming: true;
}

/** localStorage key for sidebar collapsed state. */
const SIDEBAR_COLLAPSED_KEY = 'galen-sidebar-collapsed';

/** Read sidebar collapsed state from localStorage. */
function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Persist sidebar collapsed state to localStorage. */
function writeSidebarCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  } catch {
    // Ignore storage errors (e.g. quota exceeded)
  }
}

/**
 * Strip HTML tags and decode common entities, returning clean plain text.
 * Bug #3873 Issue 2: Decode entities BEFORE stripping tags so that
 * double-encoded markup (e.g. &lt;p&gt;) is correctly removed.
 */
function stripHtml(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/<[^>]*>/g, '')
    .trim();
}

/** Suggestion chips for the empty state */
/**
 * ChatPage Component
 *
 * GALEN-CHAT-V1-R01: Accept free-text user questions via chat interface
 *                   and return LLM-generated narrative responses.
 * GALEN-CHAT-V1-R06: Optionally integrate with Semantic Search for grounding.
 * GALEN-CHAT-V1-R08: Maintain conversation history until user resets.
 * FRONTEND-WEB-V1-R05: Render streaming LLM responses incrementally.
 */
export function ChatPage(): React.ReactElement {
  const { branding } = useBranding();
  // SPEECH-V1-R02 / Bug #4340: hide the mic affordance entirely when the
  // speech module is not enabled for this deployment.
  const isSpeechEnabled = useIsFeatureEnabled('speech');
  // SPEECH-V1-R07: use the live streaming voice path when the deployment
  // enables it; otherwise the mic falls back to the batch record-then-send path.
  const isSpeechStreamingEnabled = useIsFeatureEnabled('speechStreaming');

  // State
  const [query, setQuery] = useState('');
  // Bug #5372 follow-up: lifted out of UserInputArea so the "Listening..."
  // status can render below the bordered input box instead of inside it.
  const [recordingStatus, setRecordingStatus] = useState<RecordingStatusInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // GALEN-CHAT-V1-R08: Sidebar collapse state persisted to localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);

  // Streaming placeholder (local only, not persisted until finalized)
  const [streamingMsg, setStreamingMsg] = useState<StreamingMessage | null>(null);

  // GALEN-CHAT-V1-R04: Side panel citation state (Bug #3874)
  const [panelCitation, setPanelCitation] = useState<{ citation: Citation; label: string } | null>(null);

  // Refs for auto-scroll
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // GALEN-CHAT-V1-R08: Persisted conversation state from store
  const activeConversation = useActiveConversation();
  const createConversation = useConversationStore((s) => s.createConversation);
  const addMessage = useConversationStore((s) => s.addMessage);
  const setActiveConversation = useConversationStore((s) => s.setActiveConversation);
  const setBackendConversationId = useConversationStore((s) => s.setBackendConversationId);

  // GALEN-CHAT-V1-R08: URL-based conversation routing
  const { conversationId: urlConvId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();

  // Sync URL → store: when URL has a conversation ID, activate that conversation
  useLayoutEffect(() => {
    if (!urlConvId) return; // No ID in URL — let the store drive
    if (urlConvId === activeConversation?.id) return; // Already in sync

    const exists = useConversationStore.getState().conversations.some(
      (c) => c.id === urlConvId
    );
    if (exists) {
      setActiveConversation(urlConvId);
    } else {
      // Invalid conversation ID — redirect to /chat
      void navigate('/chat', { replace: true });
    }
  }, [urlConvId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync store → URL: when active conversation changes, update URL
  useEffect(() => {
    const currentPath = activeConversation?.id
      ? `/chat/${activeConversation.id}`
      : '/chat';
    if (window.location.pathname !== currentPath) {
      void navigate(currentPath, { replace: true });
    }
  }, [activeConversation?.id, navigate]);

  // Persisted messages from the active conversation
  const persistedMessages = activeConversation?.messages ?? [];

  // FRONTEND-WEB-V1-R05: Streaming response hook
  const [streamState, startStream, cancelStream] = useStreamingResponse();

  // Track the conversation ID we're streaming into
  const streamingConvIdRef = useRef<string | null>(null);

  /**
   * Update the streaming placeholder as chunks arrive.
   * FRONTEND-WEB-V1-R05: Incremental rendering of streaming content.
   *
   * NOTE: streamingMsg is intentionally excluded from the dependency array.
   * Including it caused an infinite re-render loop in React 19 because this
   * effect calls setStreamingMsg, which would re-trigger the effect endlessly.
   * We use a ref to access the latest streaming content for finalization.
   */
  const streamingMsgRef = useRef<StreamingMessage | null>(null);
  streamingMsgRef.current = streamingMsg;

  useEffect(() => {
    if (!streamingMsgRef.current) return;

    if (streamState.content || !streamState.isStreaming) {
      setStreamingMsg((prev) =>
        prev
          ? {
              ...prev,
              content: streamState.content || prev.content,
            }
          : null
      );
    }

    // Stream finished — persist the finalized assistant message to the store
    if (!streamState.isStreaming && streamingConvIdRef.current) {
      const convId = streamingConvIdRef.current;
      const finalContent = streamState.content || streamingMsgRef.current.content;

      if (finalContent) {
        // GALEN-CHAT-V1-R03: Include citations in the persisted message
        // Bug #3861: Include is_grounded flag for citation header display
        // GALEN-CHAT-V1-R12 (Bug #4332): Prefer the backend-emitted message_id
        // so ResponseFeedback can link /api/feedback to the real persisted
        // assistant row. Fall back to a client-side id only if the backend
        // did not persist (e.g. conversation resolution failed).
        const finalMessage: ChatMessage = {
          id: streamState.messageId ?? `assistant-${Date.now()}`,
          role: 'assistant',
          content: finalContent,
          timestamp: new Date().toISOString(),
          ...(streamState.citations.length > 0
            ? { citations: streamState.citations }
            : {}),
          is_grounded: streamState.isGrounded,
        };
        addMessage(convId, finalMessage);
      }

      // Set backend conversation ID if returned
      if (streamState.conversationId) {
        setBackendConversationId(convId, streamState.conversationId);
      }

      setStreamingMsg(null);
      streamingConvIdRef.current = null;
    }
  }, [streamState.content, streamState.isStreaming, streamState.conversationId, streamState.messageId, streamState.citations, streamState.isGrounded, addMessage, setBackendConversationId]);

  // Handle stream errors
  useEffect(() => {
    if (streamState.error) {
      setError(streamState.error);
      setStreamingMsg(null);
      streamingConvIdRef.current = null;
    }
  }, [streamState.error]);

  // Auto-scroll to bottom when new messages arrive or streaming content updates
  const messageCount = persistedMessages.length;
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messageCount, streamState.content]);

  /**
   * Submit the chat question via streaming API.
   * GALEN-CHAT-V1-R01: Accept free-text user questions.
   * GALEN-CHAT-V1-R06: Optionally use search-based grounding.
   * GALEN-CHAT-V1-R08: Add messages to conversation history.
   * FRONTEND-WEB-V1-R05: Stream response incrementally.
   */
  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();

      if (!query.trim()) {
        return;
      }

      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: query.trim(),
        timestamp: new Date().toISOString(),
      };

      // Create or use existing conversation
      let convId = activeConversation?.id ?? null;
      if (!convId) {
        convId = createConversation(userMessage);
      } else {
        addMessage(convId, userMessage);
      }

      // Create a local streaming placeholder (not persisted)
      const assistantMsgId = `streaming-${Date.now()}`;
      setStreamingMsg({
        id: assistantMsgId,
        role: 'assistant',
        content: '',
        isStreaming: true,
      });
      streamingConvIdRef.current = convId;

      setQuery('');
      setError(null);

      // GALEN-CHAT-V1-R09 (Bug #4316): Thread backend conversation_id on
      // every stream request so the backend loads prior turns from the DB
      // instead of creating a new conversation row each time. Null on turn 1
      // — the backend creates the row and echoes the new id back in the done
      // event, which is captured via setBackendConversationId. The obsolete
      // conversation_history request field was dropped here; the backend
      // ignored it via extra="ignore" (Bug #4009) and multi-turn context is
      // now loaded authoritatively from DB messages keyed by conversation_id.
      const liveConversation = useConversationStore.getState().conversations.find(
        (c) => c.id === convId
      );
      const backendConversationId = liveConversation?.conversationId ?? null;

      // FRONTEND-WEB-V1-R10: Track chat query for usage metrics
      void trackUsageAction('chat_query', {
        query_text: userMessage.content,
      });

      // FRONTEND-WEB-V1-R05: Start streaming
      // GALEN-CHAT-V1-R06: Grounding always enabled for end users (GAP-006 clinical safety)
      await startStream({
        query: userMessage.content,
        use_grounding: true,
        conversation_id: backendConversationId,
      });
    },
    [query, activeConversation, createConversation, addMessage, startStream]
  );


  /**
   * Start a new chat — deactivate current conversation.
   * GALEN-CHAT-V1-R08: User can explicitly start a new conversation.
   */
  const handleNewChat = useCallback(() => {
    cancelStream();
    setStreamingMsg(null);
    streamingConvIdRef.current = null;
    setActiveConversation(null);
    setError(null);
    setSidebarOpen(false);
  }, [cancelStream, setActiveConversation]);

  /** GALEN-CHAT-V1-R08: Toggle sidebar — desktop collapses, mobile opens sheet. */
  const handleToggleSidebar = useCallback(() => {
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    if (isDesktop) {
      setSidebarCollapsed((prev) => {
        const next = !prev;
        writeSidebarCollapsed(next);
        return next;
      });
    } else {
      setSidebarOpen(true);
    }
  }, []);

  /** GALEN-CHAT-V1-R04: Open citation in side panel (Bug #3874) */
  const handleOpenCitationPanel = useCallback((citation: Citation, label: string) => {
    setPanelCitation((prev) =>
      prev?.citation.id === citation.id ? null : { citation, label }
    );
  }, []);

  const handleCloseCitationPanel = useCallback(() => {
    setPanelCitation(null);
  }, []);

  const isLoading = streamState.isStreaming;

  // Combine persisted messages with streaming placeholder for display
  const displayMessages: Array<ChatMessage | StreamingMessage> = [
    ...persistedMessages,
    ...(streamingMsg ? [streamingMsg] : []),
  ];

  return (
    <div className="-mx-4 -my-8 flex h-[calc(100vh-4rem)]">
      {/* GALEN-CHAT-V1-R08: Collapsible desktop sidebar with independent scroll */}
      <aside
        className={`hidden md:flex shrink-0 flex-col border-r bg-muted/30 overflow-hidden transition-[width] duration-200 min-h-0 ${sidebarCollapsed ? 'w-0' : 'w-72'}`}
        aria-label="Conversations"
        data-testid="desktop-sidebar"
      >
        <ConversationSidebar onNewChat={handleNewChat} />
      </aside>

      {/* Mobile sidebar (Sheet) */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="w-72 p-0">
          <SheetHeader className="sr-only">
            <SheetTitle>Conversations</SheetTitle>
          </SheetHeader>
          <ConversationSidebar onNewChat={handleNewChat} />
        </SheetContent>
      </Sheet>

      {/* Main chat area - flex column: header -> messages -> input */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Header — sidebar toggle + active conversation title */}
        <header className="shrink-0 border-b bg-background px-4 py-2">
          {/* Visually hidden h1 for WCAG heading hierarchy (FRONTEND-WEB-V1-R14) */}
          <h1 className="sr-only">Galen Chat</h1>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* GALEN-CHAT-V1-R08: Universal sidebar toggle */}
              <Button
                variant="ghost"
                size="icon"
                className="min-h-touch min-w-touch"
                onClick={handleToggleSidebar}
                aria-label="Toggle sidebar"
                data-testid="sidebar-toggle-btn"
              >
                <Menu className="h-5 w-5" aria-hidden="true" />
              </Button>
              {activeConversation?.title && (
                <span
                  className="truncate text-sm font-medium max-w-[200px]"
                  data-testid="header-conversation-title"
                >
                  {activeConversation.title}
                </span>
              )}
            </div>
            {/* GALEN-CHAT-V1-R08: New Chat button in main area (GAP-009) */}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 px-3 text-xs"
              onClick={handleNewChat}
              aria-label="New chat"
              data-testid="header-new-chat-btn"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              New Chat
            </Button>
          </div>
        </header>

        {/* Messages Area - scrollable, takes remaining space */}
        <div className="flex-1 overflow-y-auto" ref={messagesContainerRef}>
          <div className="px-4 py-6">
            {/* Error Alert */}
            {error && (
              <Alert variant="destructive" className="mb-4" role="alert">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* GALEN-CHAT-V1-R08: Conversation History */}
            {/* FRONTEND-WEB-V1-R14: WCAG 2.1 AA - aria-live region for dynamic content */}
            {displayMessages.length > 0 ? (
              <div
                className="space-y-8 pb-4"
                role="log"
                aria-live="polite"
                aria-label="Conversation history"
              >
                {displayMessages.map((message) => (
                  <div key={message.id}>
                    {message.role === 'user' ? (
                      /* User message - right-aligned bubble */
                      <div className="flex justify-end">
                        <div className="flex max-w-[80%] items-start gap-3 flex-row-reverse">
                          <div className="flex-shrink-0 flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground">
                            <User className="h-3.5 w-3.5" aria-hidden="true" />
                          </div>
                          <div>
                            <div className="rounded-2xl rounded-tr-sm bg-primary px-4 py-3 text-primary-foreground">
                              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                            </div>
                            {'timestamp' in message && message.timestamp && (
                              <p className="mt-1 text-right text-[11px] text-muted-foreground">
                                {format(new Date(message.timestamp), 'h:mm a')}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : 'isStreaming' in message && message.isStreaming ? (
                      /* FRONTEND-WEB-V1-R05: Streaming message display */
                      <div>
                        {/* Avatar row */}
                        <div className="mb-3 flex items-center gap-2">
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary">
                            <Bot className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                          </div>
                          <span className="text-sm font-medium">Galen</span>
                        </div>
                        {/* Content — full width below avatar */}
                        <div className="pl-9">
                          <StreamingResponseCard content={message.content} />
                        </div>
                      </div>
                    ) : (
                      /* Assistant response — avatar row + full-width content below */
                      <div>
                        {/* Avatar row */}
                        <div className="mb-3 flex items-center gap-2">
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary">
                            <Bot className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                          </div>
                          <span className="text-sm font-medium">Galen</span>
                          {'timestamp' in message && message.timestamp && (
                            <span className="text-[11px] text-muted-foreground">
                              {format(new Date(message.timestamp), 'h:mm a')}
                            </span>
                          )}
                        </div>
                        {/* Content — full width below avatar */}
                        <div className="pl-9">
                          <ChatResponseSection
                            response={message.content}
                            citations={'citations' in message ? (message.citations ?? []) : []}
                            isGrounded={'is_grounded' in message ? message.is_grounded : undefined}
                            onOpenCitationPanel={handleOpenCitationPanel}
                            conversationId={activeConversation?.conversationId}
                            messageId={message.id}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {/* Auto-scroll anchor */}
                <div ref={messagesEndRef} />
              </div>
            ) : (
              /* Initial State — welcoming empty state with suggestion chips */
              <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
                <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
                  <Sparkles className="h-8 w-8 text-primary" aria-hidden="true" />
                </div>
                <h2 className="text-xl font-semibold" data-testid="empty-state-heading">
                  How can I help you today?
                </h2>
                <p className="mt-2 max-w-md text-sm text-muted-foreground">
                  Ask questions about clinical guidelines and receive AI-generated
                  answers with citations to relevant guideline sections.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Input Area - fixed at bottom */}
        <div className="shrink-0 border-t bg-background px-4 py-3">
          <div>
            {/* Chat Input Form - FRONTEND-WEB-V1-R02: Text and optional audio input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void handleSubmit();
              }}
              aria-label="Galen Chat"
            >
              {/* Contained input bar */}
              <div className="rounded-2xl border bg-background shadow-sm">
                <div className="px-4 pt-3 pb-1">
                  {/* FRONTEND-WEB-V1-R02: UserInputArea with audio support */}
                  <UserInputArea
                    value={query}
                    onChange={setQuery}
                    onSubmit={() => void handleSubmit()}
                    placeholder="Ask a question about clinical guidelines..."
                    disabled={isLoading}
                    enableAudio={isSpeechEnabled}
                    streaming={isSpeechStreamingEnabled}
                    multiline={true}
                    ariaLabel="Your question"
                    textareaClassName="border-0 shadow-none focus-visible:ring-0 min-h-[44px] p-0 resize-none"
                    autoResize={true}
                    hideRecordingStatus={true}
                    onRecordingStatusChange={setRecordingStatus}
                  />
                </div>
                {/* Send button row */}
                <div className="flex items-center justify-end px-3 pb-2">
                  {/* FRONTEND-WEB-V1-R13: Touch-friendly button size (min 44px) */}
                  <Button
                    type="submit"
                    size="icon"
                    className="h-9 w-9 shrink-0 rounded-full min-h-touch min-w-touch"
                    disabled={isLoading || !query.trim()}
                    aria-label="Send question"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <Send className="h-4 w-4" aria-hidden="true" />
                    )}
                  </Button>
                </div>
              </div>
            </form>

            {/* Bug #5372 follow-up: recording status rendered below the
                bordered input box (not inside it) — status is lifted out of
                UserInputArea via onRecordingStatusChange. */}
            {recordingStatus && (
              <div
                className="mt-2 flex items-center gap-2 text-sm text-primary"
                role="status"
                aria-live="polite"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
                </span>
                {recordingStatus.label}
              </div>
            )}

            {/* GALEN-CHAT-V1-R02: Persistent clinical safety disclaimer (GAP-006) */}
            <div
              className="mt-2 flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground"
              data-testid="safety-disclaimer"
            >
              <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
              <p>
                {branding.disclaimers.safetyDisclaimer ??
                  'Galen Chat retrieves content from published clinical guidelines. It does not analyse patient data, recommend diagnostic or therapeutic actions, or interpret clinical parameters. All clinical decisions remain the sole responsibility of the healthcare professional.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* GALEN-CHAT-V1-R04: Side panel for citation detail (Bug #3874) */}
      {panelCitation && (
        <>
          {/* Mobile backdrop */}
          <div
            className="fixed inset-0 z-10 bg-black/50 md:hidden"
            onClick={handleCloseCitationPanel}
            aria-hidden="true"
          />
          <aside
            className="fixed inset-y-0 right-0 z-20 w-[85%] max-w-sm md:relative md:inset-auto md:z-auto md:w-[380px] shrink-0 flex flex-col border-l bg-background shadow-lg md:shadow-none"
            aria-label="Citation detail panel"
            data-testid="citation-side-panel"
          >
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h3 className="text-sm font-medium truncate pr-2">{panelCitation.label}</h3>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={handleCloseCitationPanel}
                aria-label="Close citation panel"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <CitationDetail citation={panelCitation.citation} index={0} />
            </div>
          </aside>
        </>
      )}
    </div>
  );
}

/**
 * StreamingResponseCard Component
 *
 * FRONTEND-WEB-V1-R05: Display streaming LLM response incrementally
 * with a polished animated indicator.
 */
interface StreamingResponseCardProps {
  content: string;
}

function StreamingResponseCard({
  content,
}: StreamingResponseCardProps): React.ReactElement {
  return (
    <div aria-label="Streaming response">
      {content ? (
        <MarkdownRenderer content={content} />
      ) : null}
      {/* Animated dots indicator */}
      <div className="mt-3 flex items-center gap-1.5" role="status">
        <span className="chat-streaming-dot h-2 w-2 rounded-full bg-primary/60" />
        <span className="chat-streaming-dot h-2 w-2 rounded-full bg-primary/60 [animation-delay:0.2s]" />
        <span className="chat-streaming-dot h-2 w-2 rounded-full bg-primary/60 [animation-delay:0.4s]" />
        <span className="ml-2 text-xs text-muted-foreground">Generating response...</span>
      </div>
    </div>
  );
}

/**
 * ChatResponseSection Component
 *
 * GALEN-CHAT-V1-R02: Format LLM responses for clinical reading.
 * GALEN-CHAT-V1-R03: Display all citations and referenced sections.
 */
interface ChatResponseSectionProps {
  response: string;
  citations: Citation[];
  isGrounded?: boolean;
  onOpenCitationPanel?: (citation: Citation, label: string) => void;
  /** GALEN-CHAT-V1-R12: Backend conversation ID for feedback linkage. */
  conversationId?: string | null;
  /** GALEN-CHAT-V1-R12: Message ID for feedback linkage. */
  messageId?: string | null;
}

function ChatResponseSection({
  response,
  citations,
  isGrounded,
  onOpenCitationPanel,
  conversationId,
  messageId,
}: ChatResponseSectionProps): React.ReactElement {
  const { branding } = useBranding();
  const [isCopied, setIsCopied] = useState(false);
  // Bug fix: track which inline citation badge was clicked so CitationBlock can auto-expand
  const [activeCitationIndex, setActiveCitationIndex] = useState<number | null>(null);

  const handleCitationClick = useCallback((citationNumber: number) => {
    // citationNumber is 1-based; convert to 0-based index for CitationBlock
    const idx = citationNumber - 1;
    // Reset first so re-clicking the same badge re-triggers the useEffect
    setActiveCitationIndex(null);
    // Use microtask to ensure the null state is committed before setting the new value
    queueMicrotask(() => setActiveCitationIndex(idx));
  }, []);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(response);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      console.error('Failed to copy to clipboard');
    }
  }, [response]);

  return (
    <div className="space-y-3">
      {/* GALEN-CHAT-V1-R02: Render markdown for clinical reading */}
      {/* GALEN-CHAT-V1-R03: Pass citation count for inline ref rendering */}
      <MarkdownRenderer content={response} citationCount={citations.length} onCitationClick={handleCitationClick} />

      {/* Action buttons: Copy + Feedback (GALEN-CHAT-V1-R12) */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-muted-foreground hover:text-foreground"
          onClick={() => void handleCopy()}
          aria-label="Copy response to clipboard"
        >
          {isCopied ? (
            <Check className="mr-1 h-3.5 w-3.5 text-green-600" />
          ) : (
            <Copy className="mr-1 h-3.5 w-3.5" />
          )}
          <span className="text-xs">{isCopied ? 'Copied' : 'Copy'}</span>
        </Button>
        <ResponseFeedback conversationId={conversationId} messageId={messageId} />
      </div>

      {/* Citations Section - GALEN-CHAT-V1-R03, GALEN-CHAT-V1-R04 */}
      {citations.length > 0 && (
        <CitationBlock citations={citations} displayMode={branding.citationDisplayMode} isGrounded={isGrounded} onOpenCitationPanel={onOpenCitationPanel} activeCitationIndex={activeCitationIndex} />
      )}

      {/* GALEN-CHAT-V1-R02: Per-response medical disclaimer (GAP-002) */}
      <p className="text-xs italic text-muted-foreground" data-testid="response-disclaimer">
        {branding.disclaimers.responseDisclaimer ??
          'This summarises guideline content relevant to the question and does not determine management for an individual patient.'}
      </p>
    </div>
  );
}

/**
 * ResponseFeedback Component
 *
 * GALEN-CHAT-V1-R12: Capture usage and conversation metrics.
 * Provides thumbs up, thumbs down, and comment buttons per response.
 * Submits structured feedback to POST /v1/feedback with conversation
 * and message linkage.
 */
interface ResponseFeedbackProps {
  /** Backend conversation ID for feedback linkage. */
  conversationId?: string | null;
  /** Message ID for feedback linkage. */
  messageId?: string | null;
}

function ResponseFeedback({ conversationId, messageId }: ResponseFeedbackProps): React.ReactElement {
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState('');

  const handleFeedback = useCallback(
    (value: 'up' | 'down') => {
      const next = feedback === value ? null : value;
      setFeedback(next);
      if (next) {
        void submitFeedback({
          conversation_id: conversationId,
          message_id: messageId,
          rating: next,
        });
      }
    },
    [feedback, conversationId, messageId]
  );

  const handleSubmitComment = useCallback(() => {
    if (!comment.trim()) return;
    void submitFeedback({
      conversation_id: conversationId,
      message_id: messageId,
      rating: feedback ?? 'up',
      comment: comment.trim(),
    });
    setComment('');
    setShowComment(false);
  }, [comment, conversationId, messageId, feedback]);

  return (
    <div className="flex items-center gap-1" data-testid="response-feedback">
      <Button
        variant="ghost"
        size="sm"
        className={`h-7 w-7 p-0 ${feedback === 'up' ? 'text-green-600' : 'text-muted-foreground hover:text-foreground'}`}
        onClick={() => handleFeedback('up')}
        aria-label="I like it"
        aria-pressed={feedback === 'up'}
      >
        <ThumbsUp className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className={`h-7 w-7 p-0 ${feedback === 'down' ? 'text-red-600' : 'text-muted-foreground hover:text-foreground'}`}
        onClick={() => handleFeedback('down')}
        aria-label="I do not like it"
        aria-pressed={feedback === 'down'}
      >
        <ThumbsDown className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className={`h-7 w-7 p-0 ${showComment ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
        onClick={() => setShowComment(!showComment)}
        aria-label="Add comment"
        aria-expanded={showComment}
      >
        <MessageSquare className="h-3.5 w-3.5" />
      </Button>
      {showComment && (
        <div className="ml-2 flex items-center gap-1">
          <input
            type="text"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmitComment();
            }}
            placeholder="Add a comment..."
            className="h-7 rounded-md border bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
            aria-label="Comment on response"
          />
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={handleSubmitComment}
            disabled={!comment.trim()}
          >
            Send
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * CitationBlock Component
 *
 * GALEN-CHAT-V1-R03: Display all citations and referenced sections.
 * GALEN-CHAT-V1-R04: Present citations in expandable format with detail view.
 *
 * Bug #3858: Citations use passthrough LLMSemanticSearch result fields.
 * Display rules:
 *   - Summary line: ShortTitle - GuidelineSectionName + truncated text snippet
 *   - Detail view (recommendation): RecommendationText with COR/LOE bands,
 *     ExplanatoryText, IndicationText
 *   - Detail view (section): SectionText only (never SummaryVersionContent)
 *   - Result type: RecommendationText present → recommendation; else → section
 */
interface CitationBlockProps {
  citations: Citation[];
  displayMode: CitationDisplayMode;
  /**
   * Whether the LLM response is grounded in retrieved documents.
   * When false, the header shows "Related search results" instead of "N references".
   * GALEN-CHAT-V1-R03 (Bug #3861)
   */
  isGrounded?: boolean;
  /** GALEN-CHAT-V1-R04 (Bug #3874): Callback to open citation in side panel */
  onOpenCitationPanel?: (citation: Citation, label: string) => void;
  /** 0-based index of the citation activated by an inline badge click. */
  activeCitationIndex?: number | null;
}

/**
 * Get a display-safe text snippet for a citation, never showing 'None'.
 * Bug #3883: Truncate to 120 chars for collapsed view.
 */
function getCitationSnippet(citation: Citation): string {
  let text = '';
  if (citation.RecommendationText) text = stripHtml(citation.RecommendationText);
  else if (citation.SectionText) text = stripHtml(citation.SectionText);
  else if (citation.GuidelineSectionName) text = stripHtml(citation.GuidelineSectionName);
  return text.length > 120 ? text.slice(0, 120) + '\u2026' : text;
}

/**
 * Get the citation summary label: ShortTitle – GuidelineSectionName.
 * Bug #3873 Issue 3: Strip HTML from fields so raw markup never shows in labels.
 */
function getCitationLabel(citation: Citation): string {
  const parts: string[] = [];
  const shortTitle = stripHtml(citation.ShortTitle);
  const sectionName = stripHtml(citation.GuidelineSectionName);
  if (shortTitle) parts.push(shortTitle);
  if (sectionName) parts.push(sectionName);
  return parts.join(' \u2013 ') || 'Citation';
}

/**
 * SpecReferenceID: GALEN-CHAT-V1
 * Requirements: GALEN-CHAT-V1-R03
 *
 * Default COR/LOE hex colours used when no admin colour mapping is configured.
 */
const DEFAULT_COR_COLORS: Record<string, string> = {
  'I': '#16a34a',
  'IIa': '#eab308',
  'IIb': '#f97316',
  'III': '#dc2626',
};

const DEFAULT_LOE_COLORS: Record<string, string> = {
  'A': '#2563eb',
  'B': '#60a5fa',
  'C': '#93c5fd',
};

const DEFAULT_MUTED_COLOR = '#6b7280';

/** Returns true if the hex colour is perceptually light (needs dark text). */
function isLightColor(hex: string): boolean {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 150;
}

/** Resolve COR badge colour from admin branding config, falling back to defaults. */
function resolveCorColor(value: string | null | undefined, mappings: { value: string; color: string }[]): string {
  if (!value) return DEFAULT_MUTED_COLOR;
  return mappings.find((m) => m.value === value)?.color ?? DEFAULT_COR_COLORS[value] ?? DEFAULT_MUTED_COLOR;
}

/** Resolve LOE badge colour from admin branding config, falling back to defaults. */
function resolveLoeColor(value: string | null | undefined, mappings: { value: string; color: string }[]): string {
  if (!value) return DEFAULT_MUTED_COLOR;
  return mappings.find((m) => m.value === value)?.color ?? DEFAULT_LOE_COLORS[value] ?? DEFAULT_MUTED_COLOR;
}

function CitationBlock({ citations, displayMode, isGrounded, onOpenCitationPanel, activeCitationIndex }: CitationBlockProps): React.ReactElement {
  const { branding } = useBranding();
  // GALEN-CHAT-V1-R04: Default to collapsed to match old app's "N references" pattern
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedCitation, setExpandedCitation] = useState<number | null>(null);

  // Auto-expand and open detail when an inline citation badge is clicked
  useEffect(() => {
    if (activeCitationIndex == null) return;
    const citation = citations[activeCitationIndex];
    if (!citation) return;

    setIsExpanded(true);

    if (displayMode === 'side_panel' && onOpenCitationPanel) {
      onOpenCitationPanel(citation, getCitationLabel(citation));
    } else {
      setExpandedCitation(activeCitationIndex);
    }

    // Scroll the citation item into view after DOM updates
    requestAnimationFrame(() => {
      document.getElementById(`citation-${activeCitationIndex + 1}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    });
  }, [activeCitationIndex, citations, displayMode, onOpenCitationPanel]);

  const handleCitationClick = useCallback((index: number) => {
    const citation = citations[index];
    if (!citation) return;

    // LOGGING-V1-R07: Fire COUNTER citation_expand tracking event
    void trackUsageAction('citation_expand', {
      document_id: citation.id,
      referrer_mode: 'chat',
      ranking_position: index + 1,
      ...(citation.ShortTitle ? { guideline_name: citation.ShortTitle } : {}),
      ...(citation.GuidelineSectionName ? { section_name: citation.GuidelineSectionName } : {}),
    });

    if (displayMode === 'side_panel' && onOpenCitationPanel) {
      onOpenCitationPanel(citation, getCitationLabel(citation));
    } else {
      setExpandedCitation(expandedCitation === index ? null : index);
    }
  }, [displayMode, onOpenCitationPanel, citations, expandedCitation]);

  return (
    <Card className="border-muted">
        <CardHeader className="px-3 py-2">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex w-full items-center justify-between text-left"
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse references' : 'Expand references'}
          >
            <div className="flex items-center gap-2">
              <ExternalLink className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <span className="text-sm font-medium">
                {isGrounded === false
                  ? 'Related search results'
                  : `${citations.length} reference${citations.length !== 1 ? 's' : ''}`}
              </span>
            </div>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        </CardHeader>
        {isExpanded && (
          <CardContent className="px-3 pb-3 pt-0">
            <ul className="space-y-2" aria-label="Citation references">
              {citations.map((citation, index) => {
                const isRecommendation = !!citation.RecommendationText;
                const snippet = getCitationSnippet(citation);
                const isDetailOpen = displayMode === 'inline' && expandedCitation === index;

                return (
                  <li
                    key={citation.id || index}
                    id={`citation-${index + 1}`}
                    className="rounded-md border p-2.5 text-sm scroll-mt-4"
                  >
                    <div className="flex items-start gap-2">
                      <span className="inline-flex h-5 min-w-5 items-center justify-center rounded bg-primary/10 text-[11px] font-semibold text-primary">
                        {index + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        {/* Summary line: clickable to open detail view */}
                        <button
                          type="button"
                          onClick={() => handleCitationClick(index)}
                          className="text-left font-medium text-primary hover:underline"
                          aria-expanded={isDetailOpen}
                          aria-label={
                            displayMode === 'side_panel'
                              ? `View citation ${index + 1} in side panel`
                              : isDetailOpen ? `Collapse citation ${index + 1}` : `Expand citation ${index + 1}`
                          }
                        >
                          {getCitationLabel(citation)}
                        </button>
                        {/* Snippet preview (only when detail is closed) */}
                        {!isDetailOpen && snippet && (
                          <p className="mt-1 text-xs text-foreground line-clamp-2">
                            {snippet}
                          </p>
                        )}
                        {/* COR/LOE badges for recommendations — GALEN-CHAT-V1-R03: uses admin-configured branding colours */}
                        {isRecommendation && !isDetailOpen && (citation.RecommendationStrength || citation.EvidenceStrength) && (
                          <div className="mt-1.5 flex items-center gap-1.5">
                            {citation.RecommendationStrength && (() => {
                              const bg = resolveCorColor(citation.RecommendationStrength, branding.corColorMappings);
                              return (
                                <span
                                  className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold"
                                  style={{ backgroundColor: bg, color: isLightColor(bg) ? '#000' : '#fff' }}
                                >
                                  COR {citation.RecommendationStrength}
                                </span>
                              );
                            })()}
                            {citation.EvidenceStrength && (() => {
                              const bg = resolveLoeColor(citation.EvidenceStrength, branding.loeColorMappings);
                              return (
                                <span
                                  className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold"
                                  style={{ backgroundColor: bg, color: isLightColor(bg) ? '#000' : '#fff' }}
                                >
                                  LOE {citation.EvidenceStrength}
                                </span>
                              );
                            })()}
                          </div>
                        )}
                        {/* Inline detail view — only when displayMode is 'inline' */}
                        {isDetailOpen && (
                          <CitationDetail citation={citation} index={index} />
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        )}
    </Card>
  );
}

/**
 * CitationDetail Component
 *
 * GALEN-CHAT-V1-R04: Renders the full detail view for a single citation.
 * Shared between inline expansion and side panel modes.
 */
interface CitationDetailProps {
  citation: Citation;
  index: number;
}

function CitationDetail({ citation, index }: CitationDetailProps): React.ReactElement {
  const { branding } = useBranding();
  const isRecommendation = !!citation.RecommendationText;
  const corBg = resolveCorColor(citation.RecommendationStrength, branding.corColorMappings);
  const loeBg = resolveLoeColor(citation.EvidenceStrength, branding.loeColorMappings);
  // BROWSE-V1-R20: Cross-module Browse link is rendered only when
  // the upstream guideline id is present on the citation. BrowseLink
  // itself gates on flags.browseLinks (R18) so no additional flag
  // check is needed here.
  const guidelineId = citation.GuidelineId ?? null;
  const guidelineSectionId = citation.GuidelineSectionId ?? null;

  return (
    <div className="mt-2 space-y-2 border-t pt-2" data-testid={`citation-detail-${index + 1}`}>
      {isRecommendation ? (
        <>
          {/* COR/LOE colour bands — GALEN-CHAT-V1-R03: uses admin-configured branding colours */}
          {(citation.RecommendationStrength || citation.EvidenceStrength) && (
            <div className="flex items-center gap-2">
              {citation.RecommendationStrength && (
                <span
                  className="inline-flex items-center rounded px-2 py-1 text-xs font-bold"
                  style={{ backgroundColor: corBg, color: isLightColor(corBg) ? '#000' : '#fff' }}
                >
                  COR {citation.RecommendationStrength}
                </span>
              )}
              {citation.EvidenceStrength && (
                <span
                  className="inline-flex items-center rounded px-2 py-1 text-xs font-bold"
                  style={{ backgroundColor: loeBg, color: isLightColor(loeBg) ? '#000' : '#fff' }}
                >
                  LOE {citation.EvidenceStrength}
                </span>
              )}
            </div>
          )}
          {/* Recommendation text — Bug #3884: render HTML with DOMPurify */}
          <SafeHtml html={citation.RecommendationText} as="div" className="text-sm prose prose-sm max-w-none" />
          {/* Explanatory text */}
          {citation.ExplanatoryText && (
            <div>
              <p className="text-xs font-medium text-muted-foreground">Explanatory</p>
              <SafeHtml html={citation.ExplanatoryText} as="div" className="text-xs text-foreground" />
            </div>
          )}
          {/* Indication text */}
          {citation.IndicationText && (
            <div>
              <p className="text-xs font-medium text-muted-foreground">Indication</p>
              <SafeHtml html={citation.IndicationText} as="div" className="text-xs text-foreground" />
            </div>
          )}
        </>
      ) : (
        <>
          {/* Section text — Bug #3884: render HTML with DOMPurify */}
          {citation.SectionText && (
            <SafeHtml html={citation.SectionText} as="div" className="text-sm prose prose-sm max-w-none" />
          )}
        </>
      )}
      {/* Page reference — render as hyperlink when value is a URL (Bug #3984) */}
      {citation.PageReference && (
        <p className="text-[11px] text-muted-foreground">
          {/^https?:\/\//i.test(citation.PageReference) ? (
            <a
              href={citation.PageReference}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              {citation.PageReference}
            </a>
          ) : (
            citation.PageReference
          )}
        </p>
      )}
      {/* BROWSE-V1-R20: Cross-module Browse link on the chat
          citation card. Rendered when the upstream guideline id is
          present; BrowseLink gates internally on flags.browseLinks
          so deployments that disable the flag see nothing. */}
      {guidelineId != null && (
        <div className="pt-1">
          <BrowseLink
            guidelineId={guidelineId}
            sectionId={guidelineSectionId}
            searchMode="chat"
            guidelineName={citation.ShortTitle}
            sectionName={citation.GuidelineSectionName}
          />
        </div>
      )}
    </div>
  );
}

export default ChatPage;
